import _ from "lodash";
import React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";

import { Checkbox } from "@/components/ui/Checkbox";
import { renderTrigger } from "@/components/ui/Dropdown/utils";
import { Check20 } from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";

import styles from "./style.module.css";

import { useIsMobile } from "@/hooks/useIsMobile";

export type DropdownSelectItem = {
  key: string;
  label: string;
  value: string | number | { value: string } | { value: number };
};

type DropdownSelectProps = {
  items: DropdownSelectItem[];
  selected?: Maybe<DropdownSelectItem> | DropdownSelectItem[];
  type?: "default" | "checkbox";
  noChevron?: boolean;
  autoHeight?: boolean;
  preventClose?: boolean;
  contentAlign?: SelectPrimitive.SelectContentProps["align"];
  contentOffset?: number;
  contentClassname?: string;
  scrollAreaClassname?: string;
  onChange: (value: DropdownSelectItem) => void;
  children: React.ReactElement;
  chevronClassName?: string;
};

export const DropdownSelect = ({
  items,
  selected,
  type = "default",
  noChevron,
  autoHeight,
  preventClose,
  contentAlign = "start",
  contentOffset,
  contentClassname,
  scrollAreaClassname,
  onChange,
  children,
  chevronClassName = "",
}: DropdownSelectProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const isMobile = useIsMobile();

  function onOpenChange() {
    // TODO(https://chorusview.atlassian.net/browse/SWE-991):
    // The reason we are doing this strange logic with the setTimeout is
    // because on mobile, if you select an item on the dropdown the touch
    // event will will simultaneously click the element behind it. This was
    // causing the LanguageSwitcher to inadvertently select an org in the
    // org switcher. Known bug for radix-ui:
    if (preventClose) return;
    if (isOpen && isMobile) {
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    } else {
      setIsOpen((prev) => !prev);
    }
  }

  function onClickOutside() {
    if (preventClose) {
      setIsOpen(false);
    }
  }

  const mode = Array.isArray(selected) ? "multiple" : "single";

  function onValueChange(value: string) {
    const item = items.find((item) => {
      const itemValue =
        typeof item.value === "object"
          ? `${item.value.value}`
          : `${item.value}`;
      return itemValue === value;
    });
    if (!item) {
      throw new Error(`Item with value ${value} not found`);
    }
    onChange(item);
  }
  return (
    <SelectPrimitive.Root
      open={isOpen}
      value={children.props.children}
      onOpenChange={onOpenChange}
      onValueChange={onValueChange}
    >
      <SelectPrimitive.Trigger asChild>
        {renderTrigger({
          children,
          isOpen,
          onClick: () => setIsOpen(true),
          noChevron,
          chevronClassName,
        })}
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          align={contentAlign}
          sideOffset={5}
          alignOffset={contentOffset}
          className={cn(
            styles["dd-content"],
            "w-48 !border-0 !p-0",
            contentClassname,
          )}
          style={{ zIndex: 1000000 }}
          onPointerDownOutside={onClickOutside}
          onEscapeKeyDown={onClickOutside}
          data-testid="dd-content"
        >
          <ScrollAreaPrimitive.Root
            type="auto"
            className={cn(
              "ScrollAreaRoot max-h-[379px] w-full",
              { "h-auto": autoHeight },
              scrollAreaClassname,
            )}
          >
            <SelectPrimitive.Viewport asChild>
              <ScrollAreaPrimitive.Viewport
                className="ScrollAreaViewport size-full"
                style={{ overflowY: undefined }}
              >
                {items.map((item) => {
                  if (mode === "multiple") {
                    return (
                      <Item
                        key={item.key}
                        type={type}
                        item={item}
                        selected={selected as DropdownSelectItem[]}
                        mode="multiple"
                      />
                    );
                  }
                  if (mode === "single") {
                    return (
                      <Item
                        key={item.key}
                        type={type}
                        item={item}
                        selected={selected as DropdownSelectItem}
                        mode="single"
                      />
                    );
                  }
                })}
              </ScrollAreaPrimitive.Viewport>
            </SelectPrimitive.Viewport>
            <ScrollAreaPrimitive.Scrollbar
              orientation="vertical"
              className="ScrollAreaScrollbar"
            >
              <ScrollAreaPrimitive.Thumb className="ScrollAreaThumb" />
            </ScrollAreaPrimitive.Scrollbar>
          </ScrollAreaPrimitive.Root>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

type ItemProps = {
  type: DropdownSelectProps["type"];
  item: DropdownSelectItem;
} & ModeProps;

type ModeProps =
  | {
      selected: DropdownSelectItem;
      mode: "single";
    }
  | {
      selected: DropdownSelectItem[];
      mode: "multiple";
    };

const Item = ({ item, selected, type = "default", mode }: ItemProps) => {
  const itemValue =
    typeof item.value === "object" ? `${item.value.value}` : `${item.value}`;
  let checked = false;
  if (mode === "multiple") {
    checked = selected.some((item) => {
      const selectedItemValue =
        typeof item.value === "object"
          ? `${item.value.value}`
          : `${item.value}`;
      return selectedItemValue === itemValue;
    });
  }
  if (mode === "single") {
    if (_.isNil(selected)) {
      checked = false;
    } else {
      const selectedItemValue =
        typeof selected.value === "object"
          ? `${selected.value.value}`
          : `${selected.value}`;
      checked = selectedItemValue === itemValue;
    }
  }

  return (
    <SelectPrimitive.Item
      key={item.key}
      value={itemValue}
      className={cn(styles["dd-item"], "[&_[id]]:flex-1 [&_[id]]:truncate")}
      data-testid="dd-item"
    >
      {type === "checkbox" && (
        <span className={cn(styles["dd-start"], "inline-flex items-center")}>
          <Checkbox isChecked={checked} />
        </span>
      )}
      <SelectPrimitive.ItemText className="flex-1">
        <span className={cn(styles["dd-label"])}>{item.label}</span>
      </SelectPrimitive.ItemText>
      {type === "default" && (
        <span className={cn(styles["dd-end"])}>
          {checked && (
            <Check20 className="fill-current" data-testid="dd-item-checked" />
          )}
        </span>
      )}
    </SelectPrimitive.Item>
  );
};
